import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

const CONSENT_STORAGE_KEY = 'moloman_cookie_consent';

const DEFAULT_SETTINGS = {
  isCookieBannerActive: true,

  bannerTitle: 'Preferenze cookie',
  bannerText:
    'Questo sito utilizza cookie tecnici necessari al proprio funzionamento. Con il tuo consenso possono essere utilizzati anche cookie analitici e di marketing.',

  preferencesTitle: 'Gestisci le preferenze cookie',
  preferencesText:
    'Puoi scegliere quali categorie autorizzare. I cookie necessari sono sempre attivi perché indispensabili al funzionamento del sito.',

  necessaryLabel: 'Cookie necessari',
  necessaryDescription:
    'Permettono il corretto funzionamento del sito e non possono essere disattivati.',
  necessaryStatus: 'Sempre attivi',

  analyticsLabel: 'Cookie analitici',
  analyticsDescription:
    'Aiutano a comprendere come viene utilizzato il sito attraverso dati statistici.',

  marketingLabel: 'Cookie di marketing',
  marketingDescription:
    'Possono essere utilizzati per mostrare contenuti e comunicazioni più pertinenti.',

  acceptAllLabel: 'Accetta tutto',
  rejectAllLabel: 'Rifiuta',
  customizeLabel: 'Personalizza',
  savePreferencesLabel: 'Salva preferenze',
  closeLabel: 'Chiudi',

  cookiePolicyLinkLabel: 'Leggi la Cookie Policy',

  consentVersion: '1.0',

  bannerBackgroundColor: '#ffffff',
  bannerTextColor: '#222222',

  primaryButtonBackgroundColor: '#df2025',
  primaryButtonTextColor: '#ffffff',

  secondaryButtonBackgroundColor: '#eeeeee',
  secondaryButtonTextColor: '#222222',
};

const HEX_COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/;

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const BannerLayer = styled.div`
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 900;

  padding: 20px;

  display: flex;
  justify-content: center;

  pointer-events: none;

  @media (max-width: 600px) {
    padding: 8px;
  }
`;

const BannerCard = styled.section`
  width: min(100%, 1180px);
  max-height: calc(100dvh - 40px);

  padding: 22px;

  border: 1px solid rgba(0, 0, 0, 0.14);
  border-radius: 18px;

  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;

  gap: 22px;

  overflow: hidden;

  background: ${({ $backgroundColor }) => $backgroundColor};

  color: ${({ $textColor }) => $textColor};

  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.28);

  pointer-events: auto;

  animation: cookie-banner-enter 0.28s ease;

  @keyframes cookie-banner-enter {
    from {
      opacity: 0;
      transform: translateY(20px);
    }

    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 850px) {
    grid-template-columns: 1fr;

    gap: 16px;
  }

  @media (max-width: 600px) {
    max-height: min(78dvh, 520px);

    padding: 14px;

    border-radius: 14px;

    gap: 12px;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const BannerContent = styled.div`
  min-width: 0;

  @media (max-width: 600px) {
    max-height: 40dvh;

    padding-right: 3px;

    overflow-x: hidden;
    overflow-y: auto;

    overscroll-behavior: contain;

    -webkit-overflow-scrolling: touch;
  }
`;

const BannerTitle = styled.h2`
  margin: 0 0 8px;

  color: inherit;

  font-size: clamp(19px, 3vw, 24px);
  font-weight: 900;
  line-height: 1.2;

  overflow-wrap: anywhere;

  @media (max-width: 600px) {
    margin-bottom: 6px;

    font-size: 20px;
  }
`;

const BannerText = styled.p`
  margin: 0;

  color: inherit;

  font-size: 14px;
  line-height: 1.6;

  white-space: pre-line;

  overflow-wrap: anywhere;

  @media (max-width: 600px) {
    font-size: 13px;
    line-height: 1.45;
  }
`;

const PolicyLink = styled.a`
  display: inline-block;

  margin-top: ${({ $noMargin }) => ($noMargin ? '0' : '10px')};

  color: inherit;

  font-size: 13px;
  font-weight: 700;
  line-height: 1.4;

  text-decoration: underline;
  text-underline-offset: 3px;

  overflow-wrap: anywhere;

  &:focus-visible {
    outline: 3px solid ${({ $focusColor }) => $focusColor};
    outline-offset: 3px;

    border-radius: 3px;
  }

  @media (max-width: 600px) {
    font-size: 12px;
  }
`;

const BannerActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;

  gap: 10px;

  @media (max-width: 850px) {
    justify-content: flex-start;
  }

  @media (max-width: 600px) {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));

    width: 100%;

    gap: 8px;

    & > button:last-child {
      grid-column: 1 / -1;
    }
  }
`;

const ActionButton = styled.button`
  min-height: 44px;

  padding: 11px 18px;

  border: 1px solid ${({ $borderColor }) => $borderColor};
  border-radius: 10px;

  background: ${({ $backgroundColor }) => $backgroundColor};

  color: ${({ $textColor }) => $textColor};

  font: inherit;
  font-size: 14px;
  font-weight: 800;
  line-height: 1.2;

  overflow-wrap: anywhere;

  cursor: pointer;

  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    filter 0.2s ease;

  &:hover {
    filter: brightness(0.95);

    transform: translateY(-2px);
  }

  &:focus-visible {
    outline: 3px solid ${({ $focusColor }) => $focusColor};
    outline-offset: 3px;
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 600px) {
    width: 100%;
    min-height: 40px;

    padding: 9px 10px;

    font-size: 13px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover,
    &:active {
      transform: none;
    }
  }
`;

const PreferencesOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1100;

  padding: 24px;

  display: flex;
  align-items: center;
  justify-content: center;

  overflow: hidden;

  overscroll-behavior: none;

  background: rgba(0, 0, 0, 0.76);

  animation: cookie-overlay-enter 0.2s ease;

  @keyframes cookie-overlay-enter {
    from {
      opacity: 0;
    }

    to {
      opacity: 1;
    }
  }

  @media (max-width: 600px) {
    padding: 0;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const PreferencesDialog = styled.section`
  width: min(100%, 760px);
  max-height: min(90dvh, 850px);
  min-height: 0;

  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 20px;

  display: flex;
  flex-direction: column;

  overflow: hidden;

  background: ${({ $backgroundColor }) => $backgroundColor};

  color: ${({ $textColor }) => $textColor};

  box-shadow: 0 28px 85px rgba(0, 0, 0, 0.45);

  animation: cookie-dialog-enter 0.24s ease;

  @keyframes cookie-dialog-enter {
    from {
      opacity: 0;
      transform: translateY(18px) scale(0.98);
    }

    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  &:focus {
    outline: none;
  }

  @media (max-width: 600px) {
    width: 100%;
    height: 100dvh;
    max-height: 100dvh;

    border: 0;
    border-radius: 0;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const PreferencesHeader = styled.header`
  position: relative;

  flex-shrink: 0;

  padding: 24px 76px 20px 26px;

  border-bottom: 1px solid rgba(0, 0, 0, 0.13);

  @media (max-width: 600px) {
    padding: 16px 62px 14px 16px;
  }
`;

const PreferencesTitle = styled.h2`
  margin: 0;

  color: inherit;

  font-size: clamp(22px, 4vw, 30px);
  font-weight: 900;
  line-height: 1.2;

  overflow-wrap: anywhere;

  @media (max-width: 600px) {
    font-size: 22px;
  }
`;

const PreferencesIntro = styled.p`
  margin: 12px 0 0;

  color: inherit;

  font-size: 14px;
  line-height: 1.6;

  white-space: pre-line;

  overflow-wrap: anywhere;

  @media (max-width: 600px) {
    margin-top: 8px;

    font-size: 13px;
    line-height: 1.45;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 18px;
  right: 20px;

  width: 42px;
  height: 42px;

  padding: 0;

  border: 1px solid currentColor;
  border-radius: 50%;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  background: transparent;

  color: inherit;

  font-size: 28px;
  font-weight: 300;
  line-height: 1;

  cursor: pointer;

  transition:
    transform 0.2s ease,
    background 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.07);

    transform: rotate(6deg);
  }

  &:focus-visible {
    outline: 3px solid ${({ $focusColor }) => $focusColor};
    outline-offset: 3px;
  }

  @media (max-width: 600px) {
    top: 10px;
    right: 12px;

    width: 40px;
    height: 40px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover {
      transform: none;
    }
  }
`;

const PreferencesBody = styled.div`
  flex: 1;
  min-height: 0;

  padding: 22px 26px 28px;

  overflow-x: hidden;
  overflow-y: auto;

  overscroll-behavior: contain;

  touch-action: pan-y;

  -webkit-overflow-scrolling: touch;

  @media (max-width: 600px) {
    padding: 12px;
  }
`;

const CategoryList = styled.div`
  display: grid;

  gap: 14px;

  @media (max-width: 600px) {
    gap: 10px;
  }
`;

const CategoryCard = styled.div`
  min-width: 0;

  padding: 18px;

  border: 1px solid rgba(0, 0, 0, 0.14);
  border-radius: 14px;

  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;

  gap: 18px;

  @media (max-width: 600px) {
    padding: 13px;

    gap: 12px;
  }
`;

const CategoryContent = styled.div`
  min-width: 0;
`;

const CategoryTitle = styled.h3`
  margin: 0;

  color: inherit;

  font-size: 17px;
  font-weight: 900;
  line-height: 1.3;

  overflow-wrap: anywhere;

  @media (max-width: 600px) {
    font-size: 16px;
  }
`;

const CategoryDescription = styled.p`
  margin: 7px 0 0;

  color: inherit;

  font-size: 13px;
  line-height: 1.55;

  opacity: 0.82;

  overflow-wrap: anywhere;

  @media (max-width: 600px) {
    margin-top: 5px;

    font-size: 12px;
    line-height: 1.42;
  }
`;

const NecessaryStatus = styled.span`
  display: inline-block;

  margin-top: 9px;

  color: inherit;

  font-size: 12px;
  font-weight: 800;
  line-height: 1.3;

  @media (max-width: 600px) {
    margin-top: 6px;

    font-size: 11px;
  }
`;

const SwitchButton = styled.button`
  position: relative;

  width: 58px;
  height: 32px;

  padding: 0;

  border: 0;
  border-radius: 999px;

  flex-shrink: 0;

  background: ${({ $checked, $activeColor }) => ($checked ? $activeColor : '#a8a8a8')};

  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};

  opacity: ${({ disabled }) => (disabled ? 0.72 : 1)};

  transition:
    background 0.2s ease,
    opacity 0.2s ease;

  &:focus-visible {
    outline: 3px solid ${({ $focusColor }) => $focusColor};
    outline-offset: 3px;
  }

  @media (max-width: 600px) {
    width: 52px;
    height: 30px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const SwitchKnob = styled.span`
  position: absolute;
  top: 4px;
  left: ${({ $checked }) => ($checked ? '30px' : '4px')};

  width: 24px;
  height: 24px;

  border-radius: 50%;

  background: #ffffff;

  box-shadow: 0 2px 7px rgba(0, 0, 0, 0.28);

  transition: left 0.2s ease;

  @media (max-width: 600px) {
    top: 4px;
    left: ${({ $checked }) => ($checked ? '26px' : '4px')};

    width: 22px;
    height: 22px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const PreferencesFooter = styled.footer`
  flex-shrink: 0;

  padding: 18px 26px 22px;

  border-top: 1px solid rgba(0, 0, 0, 0.13);

  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;

  gap: 12px;

  @media (max-width: 600px) {
    padding: 12px;

    display: grid;
    grid-template-columns: 1fr;

    gap: 10px;
  }
`;

const FooterButtonGroup = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;

  gap: 10px;

  @media (max-width: 600px) {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));

    width: 100%;

    gap: 8px;
  }

  @media (max-width: 350px) {
    grid-template-columns: 1fr;
  }
`;

function normalizeText(value, fallback) {
  if (typeof value !== 'string') {
    return fallback;
  }

  const normalizedValue = value.trim();

  return normalizedValue || fallback;
}

function normalizeBoolean(value, fallback) {
  return typeof value === 'boolean' ? value : fallback;
}

function normalizeColor(value, fallback) {
  if (typeof value !== 'string') {
    return fallback;
  }

  const normalizedValue = value.trim();

  return HEX_COLOR_PATTERN.test(normalizedValue) ? normalizedValue : fallback;
}

function normalizeConsentVersion(value) {
  if (typeof value !== 'string' && typeof value !== 'number') {
    return DEFAULT_SETTINGS.consentVersion;
  }

  const normalizedValue = String(value).trim();

  return normalizedValue || DEFAULT_SETTINGS.consentVersion;
}

function normalizeSettings(legalSettings) {
  return {
    isCookieBannerActive: normalizeBoolean(
      legalSettings?.is_cookie_banner_active,
      DEFAULT_SETTINGS.isCookieBannerActive,
    ),

    bannerTitle: normalizeText(legalSettings?.cookie_banner_title, DEFAULT_SETTINGS.bannerTitle),

    bannerText: normalizeText(legalSettings?.cookie_banner_text, DEFAULT_SETTINGS.bannerText),

    preferencesTitle: normalizeText(
      legalSettings?.preferences_title,
      DEFAULT_SETTINGS.preferencesTitle,
    ),

    preferencesText: normalizeText(
      legalSettings?.preferences_text,
      DEFAULT_SETTINGS.preferencesText,
    ),

    necessaryLabel: normalizeText(legalSettings?.necessary_label, DEFAULT_SETTINGS.necessaryLabel),

    necessaryDescription: normalizeText(
      legalSettings?.necessary_description,
      DEFAULT_SETTINGS.necessaryDescription,
    ),

    necessaryStatus: normalizeText(
      legalSettings?.necessary_status_label,
      DEFAULT_SETTINGS.necessaryStatus,
    ),

    analyticsLabel: normalizeText(legalSettings?.analytics_label, DEFAULT_SETTINGS.analyticsLabel),

    analyticsDescription: normalizeText(
      legalSettings?.analytics_description,
      DEFAULT_SETTINGS.analyticsDescription,
    ),

    marketingLabel: normalizeText(legalSettings?.marketing_label, DEFAULT_SETTINGS.marketingLabel),

    marketingDescription: normalizeText(
      legalSettings?.marketing_description,
      DEFAULT_SETTINGS.marketingDescription,
    ),

    acceptAllLabel: normalizeText(legalSettings?.accept_all_label, DEFAULT_SETTINGS.acceptAllLabel),

    rejectAllLabel: normalizeText(
      legalSettings?.reject_optional_label,
      DEFAULT_SETTINGS.rejectAllLabel,
    ),

    customizeLabel: normalizeText(legalSettings?.customize_label, DEFAULT_SETTINGS.customizeLabel),

    savePreferencesLabel: normalizeText(
      legalSettings?.save_preferences_label,
      DEFAULT_SETTINGS.savePreferencesLabel,
    ),

    closeLabel: normalizeText(legalSettings?.close_label, DEFAULT_SETTINGS.closeLabel),

    cookiePolicyLinkLabel: normalizeText(
      legalSettings?.cookie_policy_link_label,
      DEFAULT_SETTINGS.cookiePolicyLinkLabel,
    ),

    consentVersion: normalizeConsentVersion(legalSettings?.consent_version),

    bannerBackgroundColor: normalizeColor(
      legalSettings?.cookie_banner_background_color,
      DEFAULT_SETTINGS.bannerBackgroundColor,
    ),

    bannerTextColor: normalizeColor(
      legalSettings?.cookie_banner_text_color,
      DEFAULT_SETTINGS.bannerTextColor,
    ),

    primaryButtonBackgroundColor: normalizeColor(
      legalSettings?.primary_button_background_color,
      DEFAULT_SETTINGS.primaryButtonBackgroundColor,
    ),

    primaryButtonTextColor: normalizeColor(
      legalSettings?.primary_button_text_color,
      DEFAULT_SETTINGS.primaryButtonTextColor,
    ),

    secondaryButtonBackgroundColor: normalizeColor(
      legalSettings?.secondary_button_background_color,
      DEFAULT_SETTINGS.secondaryButtonBackgroundColor,
    ),

    secondaryButtonTextColor: normalizeColor(
      legalSettings?.secondary_button_text_color,
      DEFAULT_SETTINGS.secondaryButtonTextColor,
    ),
  };
}

function removeStoredConsent() {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.removeItem(CONSENT_STORAGE_KEY);
  } catch {
    // localStorage может быть недоступен.
  }
}

function readStoredConsent(consentVersion) {
  if (typeof window === 'undefined') {
    return null;
  }

  const normalizedConsentVersion = normalizeConsentVersion(consentVersion);

  try {
    const storedValue = window.localStorage.getItem(CONSENT_STORAGE_KEY);

    if (!storedValue) {
      return null;
    }

    const parsedValue = JSON.parse(storedValue);

    const rawStoredVersion = parsedValue?.version;

    const storedVersionIsValid =
      (typeof rawStoredVersion === 'string' && rawStoredVersion.trim().length > 0) ||
      (typeof rawStoredVersion === 'number' && Number.isFinite(rawStoredVersion));

    const storedVersion = storedVersionIsValid ? String(rawStoredVersion).trim() : null;

    const timestampIsValid =
      typeof parsedValue?.timestamp === 'string' &&
      !Number.isNaN(Date.parse(parsedValue.timestamp));

    const categories = parsedValue?.categories;

    const consentIsValid =
      parsedValue &&
      typeof parsedValue === 'object' &&
      storedVersionIsValid &&
      storedVersion === normalizedConsentVersion &&
      timestampIsValid &&
      categories &&
      typeof categories === 'object' &&
      categories.necessary === true &&
      typeof categories.analytics === 'boolean' &&
      typeof categories.marketing === 'boolean';

    if (!consentIsValid) {
      removeStoredConsent();

      return null;
    }

    return {
      version: storedVersion,
      timestamp: parsedValue.timestamp,
      categories: {
        necessary: true,
        analytics: categories.analytics,
        marketing: categories.marketing,
      },
    };
  } catch {
    removeStoredConsent();

    return null;
  }
}

function writeStoredConsent(consent) {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consent));

    const storedConsent = readStoredConsent(consent.version);

    return Boolean(storedConsent);
  } catch {
    return false;
  }
}

function createConsentSnapshot(settings) {
  if (!settings.isCookieBannerActive) {
    return {
      displayMode: 'hidden',
      analyticsEnabled: false,
      marketingEnabled: false,
      hasValidStoredConsent: false,
    };
  }

  const storedConsent = readStoredConsent(settings.consentVersion);

  if (!storedConsent) {
    return {
      displayMode: 'banner',
      analyticsEnabled: false,
      marketingEnabled: false,
      hasValidStoredConsent: false,
    };
  }

  return {
    displayMode: 'hidden',
    analyticsEnabled: storedConsent.categories.analytics,
    marketingEnabled: storedConsent.categories.marketing,
    hasValidStoredConsent: true,
  };
}

function getFocusableElements(container) {
  if (!(container instanceof HTMLElement)) {
    return [];
  }

  return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter((element) => {
    if (!(element instanceof HTMLElement)) {
      return false;
    }

    const computedStyle = window.getComputedStyle(element);

    return computedStyle.display !== 'none' && computedStyle.visibility !== 'hidden';
  });
}

function focusWithoutScrolling(element) {
  if (!(element instanceof HTMLElement)) {
    return;
  }

  try {
    element.focus({
      preventScroll: true,
    });
  } catch {
    element.focus();
  }
}

function CookieConsent({ legalSettings, preferencesRequest = 0, onConsentChange }) {
  const settings = useMemo(() => normalizeSettings(legalSettings), [legalSettings]);

  const initialSnapshotRef = useRef(null);

  if (initialSnapshotRef.current === null) {
    initialSnapshotRef.current = createConsentSnapshot(settings);
  }

  const initialSnapshot = initialSnapshotRef.current;

  const [displayMode, setDisplayMode] = useState(initialSnapshot.displayMode);

  const [analyticsEnabled, setAnalyticsEnabled] = useState(initialSnapshot.analyticsEnabled);

  const [marketingEnabled, setMarketingEnabled] = useState(initialSnapshot.marketingEnabled);

  const [hasValidStoredConsent, setHasValidStoredConsent] = useState(
    initialSnapshot.hasValidStoredConsent,
  );

  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);
  const previousFocusRef = useRef(null);

  const lastPreferencesRequestRef = useRef(preferencesRequest);

  const bannerTitleId = useId();
  const bannerTextId = useId();
  const preferencesTitleId = useId();
  const preferencesTextId = useId();

  useEffect(() => {
    const snapshot = createConsentSnapshot(settings);

    setAnalyticsEnabled(snapshot.analyticsEnabled);

    setMarketingEnabled(snapshot.marketingEnabled);

    setHasValidStoredConsent(snapshot.hasValidStoredConsent);

    setDisplayMode((currentMode) => {
      if (!settings.isCookieBannerActive) {
        return 'hidden';
      }

      if (currentMode === 'preferences') {
        return currentMode;
      }

      return snapshot.displayMode;
    });
  }, [settings]);

  useEffect(() => {
    if (!settings.isCookieBannerActive || !preferencesRequest) {
      lastPreferencesRequestRef.current = preferencesRequest;

      return;
    }

    if (lastPreferencesRequestRef.current === preferencesRequest) {
      return;
    }

    lastPreferencesRequestRef.current = preferencesRequest;

    const snapshot = createConsentSnapshot(settings);

    setAnalyticsEnabled(snapshot.analyticsEnabled);

    setMarketingEnabled(snapshot.marketingEnabled);

    setHasValidStoredConsent(snapshot.hasValidStoredConsent);

    setDisplayMode('preferences');
  }, [preferencesRequest, settings]);

  useEffect(() => {
    if (displayMode !== 'preferences') {
      return undefined;
    }

    const documentElement = window.document.documentElement;

    const body = window.document.body;

    const scrollX = window.scrollX;
    const scrollY = window.scrollY;

    previousFocusRef.current = window.document.activeElement;

    const previousDocumentOverflow = documentElement.style.overflow;

    const previousDocumentOverscroll = documentElement.style.overscrollBehavior;

    const previousBodyPosition = body.style.position;

    const previousBodyTop = body.style.top;
    const previousBodyLeft = body.style.left;
    const previousBodyRight = body.style.right;
    const previousBodyWidth = body.style.width;
    const previousBodyOverflow = body.style.overflow;

    const previousBodyPaddingRight = body.style.paddingRight;

    const previousBodyOverscroll = body.style.overscrollBehavior;

    const scrollbarWidth = window.innerWidth - documentElement.clientWidth;

    if (scrollbarWidth > 0) {
      const currentPaddingRight =
        Number.parseFloat(window.getComputedStyle(body).paddingRight) || 0;

      body.style.paddingRight = `${currentPaddingRight + scrollbarWidth}px`;
    }

    documentElement.style.overflow = 'hidden';

    documentElement.style.overscrollBehavior = 'none';

    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = `-${scrollX}px`;
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    body.style.overscrollBehavior = 'none';

    const focusAnimationFrame = window.requestAnimationFrame(() => {
      focusWithoutScrolling(closeButtonRef.current);
    });

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();

        setDisplayMode(hasValidStoredConsent ? 'hidden' : 'banner');

        return;
      }

      if (event.key !== 'Tab' || !dialogRef.current) {
        return;
      }

      const focusableElements = getFocusableElements(dialogRef.current);

      if (focusableElements.length === 0) {
        event.preventDefault();

        focusWithoutScrolling(dialogRef.current);

        return;
      }

      const firstElement = focusableElements[0];

      const lastElement = focusableElements[focusableElements.length - 1];

      const activeElement = window.document.activeElement;

      const activeElementIsInside =
        activeElement instanceof Node && dialogRef.current.contains(activeElement);

      if (!activeElementIsInside) {
        event.preventDefault();

        focusWithoutScrolling(event.shiftKey ? lastElement : firstElement);

        return;
      }

      if (event.shiftKey && activeElement === firstElement) {
        event.preventDefault();

        focusWithoutScrolling(lastElement);

        return;
      }

      if (!event.shiftKey && activeElement === lastElement) {
        event.preventDefault();

        focusWithoutScrolling(firstElement);
      }
    };

    window.document.addEventListener('keydown', handleKeyDown);

    return () => {
      window.cancelAnimationFrame(focusAnimationFrame);

      window.document.removeEventListener('keydown', handleKeyDown);

      documentElement.style.overflow = previousDocumentOverflow;

      documentElement.style.overscrollBehavior = previousDocumentOverscroll;

      body.style.position = previousBodyPosition;

      body.style.top = previousBodyTop;
      body.style.left = previousBodyLeft;
      body.style.right = previousBodyRight;
      body.style.width = previousBodyWidth;

      body.style.overflow = previousBodyOverflow;

      body.style.paddingRight = previousBodyPaddingRight;

      body.style.overscrollBehavior = previousBodyOverscroll;

      window.scrollTo(scrollX, scrollY);

      const elementToRestore = previousFocusRef.current;

      window.requestAnimationFrame(() => {
        if (elementToRestore instanceof HTMLElement && window.document.contains(elementToRestore)) {
          focusWithoutScrolling(elementToRestore);
        }
      });
    };
  }, [displayMode, hasValidStoredConsent]);

  const saveConsent = (analytics, marketing) => {
    const consent = {
      version: settings.consentVersion,
      timestamp: new Date().toISOString(),
      categories: {
        necessary: true,
        analytics,
        marketing,
      },
    };

    const wasPersisted = writeStoredConsent(consent);

    setAnalyticsEnabled(analytics);
    setMarketingEnabled(marketing);

    setHasValidStoredConsent(wasPersisted);

    setDisplayMode('hidden');

    if (typeof onConsentChange === 'function') {
      onConsentChange({
        ...consent,
        persisted: wasPersisted,
      });
    }
  };

  const handleAcceptAll = () => {
    saveConsent(true, true);
  };

  const handleRejectAll = () => {
    saveConsent(false, false);
  };

  const handleSavePreferences = () => {
    saveConsent(analyticsEnabled, marketingEnabled);
  };

  const handleOpenPreferences = () => {
    setDisplayMode('preferences');
  };

  const handleClosePreferences = () => {
    setDisplayMode(hasValidStoredConsent ? 'hidden' : 'banner');
  };

  const handleCookiePolicyClick = () => {
    if (displayMode !== 'preferences') {
      return;
    }

    setDisplayMode(hasValidStoredConsent ? 'hidden' : 'banner');
  };

  if (typeof window === 'undefined' || !window.document.body || !settings.isCookieBannerActive) {
    return null;
  }

  const secondaryBorderColor = settings.secondaryButtonTextColor;

  const banner =
    displayMode === 'banner'
      ? createPortal(
          <BannerLayer>
            <BannerCard
              role="region"
              aria-labelledby={bannerTitleId}
              aria-describedby={bannerTextId}
              $backgroundColor={settings.bannerBackgroundColor}
              $textColor={settings.bannerTextColor}
            >
              <BannerContent>
                <BannerTitle id={bannerTitleId}>{settings.bannerTitle}</BannerTitle>

                <BannerText id={bannerTextId}>{settings.bannerText}</BannerText>

                <PolicyLink
                  href="#cookie-policy"
                  $focusColor={settings.primaryButtonBackgroundColor}
                >
                  {settings.cookiePolicyLinkLabel}
                </PolicyLink>
              </BannerContent>

              <BannerActions>
                <ActionButton
                  type="button"
                  onClick={handleRejectAll}
                  $backgroundColor={settings.secondaryButtonBackgroundColor}
                  $textColor={settings.secondaryButtonTextColor}
                  $borderColor={secondaryBorderColor}
                  $focusColor={settings.primaryButtonBackgroundColor}
                >
                  {settings.rejectAllLabel}
                </ActionButton>

                <ActionButton
                  type="button"
                  onClick={handleOpenPreferences}
                  $backgroundColor={settings.secondaryButtonBackgroundColor}
                  $textColor={settings.secondaryButtonTextColor}
                  $borderColor={secondaryBorderColor}
                  $focusColor={settings.primaryButtonBackgroundColor}
                >
                  {settings.customizeLabel}
                </ActionButton>

                <ActionButton
                  type="button"
                  onClick={handleAcceptAll}
                  $backgroundColor={settings.primaryButtonBackgroundColor}
                  $textColor={settings.primaryButtonTextColor}
                  $borderColor={settings.primaryButtonBackgroundColor}
                  $focusColor={settings.primaryButtonBackgroundColor}
                >
                  {settings.acceptAllLabel}
                </ActionButton>
              </BannerActions>
            </BannerCard>
          </BannerLayer>,
          window.document.body,
        )
      : null;

  const preferences =
    displayMode === 'preferences'
      ? createPortal(
          <PreferencesOverlay>
            <PreferencesDialog
              ref={dialogRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby={preferencesTitleId}
              aria-describedby={preferencesTextId}
              tabIndex={-1}
              $backgroundColor={settings.bannerBackgroundColor}
              $textColor={settings.bannerTextColor}
            >
              <PreferencesHeader>
                <PreferencesTitle id={preferencesTitleId}>
                  {settings.preferencesTitle}
                </PreferencesTitle>

                <PreferencesIntro id={preferencesTextId}>
                  {settings.preferencesText}
                </PreferencesIntro>

                <CloseButton
                  ref={closeButtonRef}
                  type="button"
                  aria-label={settings.closeLabel}
                  title={settings.closeLabel}
                  onClick={handleClosePreferences}
                  $focusColor={settings.primaryButtonBackgroundColor}
                >
                  <span aria-hidden="true">×</span>
                </CloseButton>
              </PreferencesHeader>

              <PreferencesBody>
                <CategoryList>
                  <CategoryCard>
                    <CategoryContent>
                      <CategoryTitle>{settings.necessaryLabel}</CategoryTitle>

                      <CategoryDescription>{settings.necessaryDescription}</CategoryDescription>

                      <NecessaryStatus>{settings.necessaryStatus}</NecessaryStatus>
                    </CategoryContent>

                    <SwitchButton
                      type="button"
                      role="switch"
                      aria-checked="true"
                      aria-label={settings.necessaryLabel}
                      disabled
                      $checked
                      $activeColor={settings.primaryButtonBackgroundColor}
                      $focusColor={settings.primaryButtonBackgroundColor}
                    >
                      <SwitchKnob aria-hidden="true" $checked />
                    </SwitchButton>
                  </CategoryCard>

                  <CategoryCard>
                    <CategoryContent>
                      <CategoryTitle>{settings.analyticsLabel}</CategoryTitle>

                      <CategoryDescription>{settings.analyticsDescription}</CategoryDescription>
                    </CategoryContent>

                    <SwitchButton
                      type="button"
                      role="switch"
                      aria-checked={analyticsEnabled}
                      aria-label={settings.analyticsLabel}
                      onClick={() => setAnalyticsEnabled((currentValue) => !currentValue)}
                      $checked={analyticsEnabled}
                      $activeColor={settings.primaryButtonBackgroundColor}
                      $focusColor={settings.primaryButtonBackgroundColor}
                    >
                      <SwitchKnob aria-hidden="true" $checked={analyticsEnabled} />
                    </SwitchButton>
                  </CategoryCard>

                  <CategoryCard>
                    <CategoryContent>
                      <CategoryTitle>{settings.marketingLabel}</CategoryTitle>

                      <CategoryDescription>{settings.marketingDescription}</CategoryDescription>
                    </CategoryContent>

                    <SwitchButton
                      type="button"
                      role="switch"
                      aria-checked={marketingEnabled}
                      aria-label={settings.marketingLabel}
                      onClick={() => setMarketingEnabled((currentValue) => !currentValue)}
                      $checked={marketingEnabled}
                      $activeColor={settings.primaryButtonBackgroundColor}
                      $focusColor={settings.primaryButtonBackgroundColor}
                    >
                      <SwitchKnob aria-hidden="true" $checked={marketingEnabled} />
                    </SwitchButton>
                  </CategoryCard>
                </CategoryList>
              </PreferencesBody>

              <PreferencesFooter>
                <PolicyLink
                  href="#cookie-policy"
                  onClick={handleCookiePolicyClick}
                  $noMargin
                  $focusColor={settings.primaryButtonBackgroundColor}
                >
                  {settings.cookiePolicyLinkLabel}
                </PolicyLink>

                <FooterButtonGroup>
                  <ActionButton
                    type="button"
                    onClick={handleRejectAll}
                    $backgroundColor={settings.secondaryButtonBackgroundColor}
                    $textColor={settings.secondaryButtonTextColor}
                    $borderColor={secondaryBorderColor}
                    $focusColor={settings.primaryButtonBackgroundColor}
                  >
                    {settings.rejectAllLabel}
                  </ActionButton>

                  <ActionButton
                    type="button"
                    onClick={handleSavePreferences}
                    $backgroundColor={settings.primaryButtonBackgroundColor}
                    $textColor={settings.primaryButtonTextColor}
                    $borderColor={settings.primaryButtonBackgroundColor}
                    $focusColor={settings.primaryButtonBackgroundColor}
                  >
                    {settings.savePreferencesLabel}
                  </ActionButton>
                </FooterButtonGroup>
              </PreferencesFooter>
            </PreferencesDialog>
          </PreferencesOverlay>,
          window.document.body,
        )
      : null;

  return (
    <>
      {banner}
      {preferences}
    </>
  );
}

export default CookieConsent;
